import { AccountType } from 'src/utils/enums/payment.enum';
import { formatDate } from 'src/utils/formatDate';
import { formatMoney } from 'src/utils/formatMoney';
import { numberOrDefault } from 'src/utils/helpers';
import { useLocalization } from 'src/utils/useLocalization';

import './styles.scss';

interface IProps {
    privateFlag: boolean;
    account: any;
    quoteDetails: any;
}

enum DataType {
    MONEY,
    DATE,
    NONE,
}

const AccountDetails = ({ privateFlag, account, quoteDetails }: IProps) => {
    const booleanPrivateString = privateFlag === true;
    const data = account;

    const { payoffQuotesDetail, payoffQuotesAccount, documents } =
        useLocalization();

    function getPrivateLabelOrValue(value: string, type: DataType) {
        function getValue() {
            switch (type) {
                case DataType.DATE:
                    return formatDate(value);
                case DataType.MONEY:
                    return formatMoney(value);
                case DataType.NONE:
                    return value;
            }
        }

        return booleanPrivateString ? payoffQuotesDetail.PRIVATE : getValue();
    }

    const gstHst =
        numberOrDefault(quoteDetails.residualTax) +
        numberOrDefault(quoteDetails.futurePaymentTaxAmt);

    return (
        <section className='c-form-section payment-account-info'>
            <div className='c-form-section__content'>
                <div className='title'>
                    <h6>
                        <span className='c-section-heading__title-text u-mr-xxsmall section-title'>
                            {payoffQuotesAccount.TITLE}
                        </span>
                    </h6>
                </div>
                <div className='o-fieldset__row u-mt-large u-mb-large'>
                    <div className='o-layout'>
                        <div className='o-layout__item  u-1/2  u-1/1@l'>
                            <div className='field-show'>
                                <span className='label'>
                                    {payoffQuotesAccount.TERM}
                                </span>
                                <span className='value'>
                                    {getPrivateLabelOrValue(
                                        data.contractTerm,
                                        DataType.NONE,
                                    )}
                                </span>
                            </div>
                            {data.accountType != AccountType.RetailLoan && (
                                <div className='field-show'>
                                    <span className='label'>
                                        {payoffQuotesAccount.MATURITYDATE}
                                    </span>
                                    <span className='value'>
                                        {getPrivateLabelOrValue(
                                            data.originalMaturityDate,
                                            DataType.DATE,
                                        )}
                                    </span>
                                </div>
                            )}

                            <div className='field-show'>
                                <span className='label'>
                                    {payoffQuotesAccount.FINALDATE}
                                </span>
                                <span className='value'>
                                    {getPrivateLabelOrValue(
                                        data.finalPaymentDate,
                                        DataType.DATE,
                                    )}
                                </span>
                            </div>
                            <div className='field-show'>
                                <span className='label'>
                                    {payoffQuotesAccount.SCHPAYMENT}
                                </span>
                                <span className='value'>
                                    {getPrivateLabelOrValue(
                                        data.scheduledPaymentAmount,
                                        DataType.MONEY,
                                    )}
                                </span>
                            </div>
                            <div className='field-show'>
                                <span className='label'>
                                    {payoffQuotesAccount.REMPAYMENT}
                                </span>
                                <span className='value'>
                                    {getPrivateLabelOrValue(
                                        data.remainingPayments,
                                        DataType.NONE,
                                    )}
                                </span>
                            </div>
                            <div className='field-show'>
                                <span className='label'>
                                    {payoffQuotesAccount.NEXTDATE}
                                </span>
                                <span className='value'>
                                    {getPrivateLabelOrValue(
                                        data.nextPaymemtDate,
                                        DataType.DATE,
                                    )}
                                </span>
                            </div>
                        </div>
                        <div className='o-layout__item  u-1/2  u-1/1@l'>
                            {data.accountType == AccountType.OperatingLease && (
                                <div className='field-show'>
                                    <span className='label'>
                                        {
                                            documents.DOCUMENTS_VIEW_NAMES_REFUNDABLE
                                        }
                                    </span>
                                    <span className='value'>
                                        {formatMoney(
                                            quoteDetails.securityDeposit,
                                        )}
                                    </span>
                                </div>
                            )}
                            <div className='field-show'>
                                <span className='label'>
                                    {payoffQuotesAccount.LASTPAYMENT}
                                </span>
                                <span className='value'>
                                    {getPrivateLabelOrValue(
                                        data.lastPaymentReceived,
                                        DataType.MONEY,
                                    )}
                                </span>
                            </div>
                            <div className='field-show'>
                                <span className='label'>
                                    {payoffQuotesAccount.LASTDATE}
                                </span>
                                <span className='value'>
                                    {formatDate(data.lastPaymentDate)}
                                </span>
                            </div>
                            <div className='field-show'>
                                <span className='label'>
                                    {payoffQuotesAccount.BALANCE}
                                </span>
                                <span className='value'>
                                    {getPrivateLabelOrValue(
                                        data.currentBalance,
                                        DataType.MONEY,
                                    )}
                                </span>
                            </div>
                            {data.accountType == AccountType.OperatingLease && (
                                <>
                                    <div className='field-show'>
                                        <span className='label'>
                                            {payoffQuotesAccount.AMTBEFORETAX}
                                        </span>
                                        <span className='value'>
                                            {getPrivateLabelOrValue(
                                                data.amountBeforeTax,
                                                DataType.MONEY,
                                            )}
                                        </span>
                                    </div>
                                    <div className='field-show'>
                                        <span className='label'>
                                            {payoffQuotesAccount.GST_HST}
                                        </span>
                                        <span className='value'>
                                            {getPrivateLabelOrValue(
                                                gstHst.toString(),
                                                DataType.MONEY,
                                            )}
                                        </span>
                                    </div>
                                    <div className='field-show'>
                                        <span className='label'>
                                            {payoffQuotesAccount.OTHER_AMOUNT}
                                        </span>
                                        <span className='value'>
                                            {formatMoney(
                                                quoteDetails.paymentDueNotPaid,
                                            )}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AccountDetails;
